import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { dashboardApi } from '@/api/client';
import { useAuth } from '@/contexts/AuthContext';
import { fmtFixed, intlLocale } from '@/lib/format';
import { phaseKey, regimeWidgetVisible, type RegimeLiveResponse } from '@/lib/regimeWidget';

/****************
 * Market conditions, from the regime tensor (2026-10-04).
 *
 * Guillaume, 2026-10-01: the live regime reading, every 1-5 minutes, shown on
 * the dashboard with the Easy-Hard index, the Volatility index and the
 * Regime. Admins only until REGIME_WIDGET_USERS names someone else, so he
 * sees it before users do.
 *
 * Shown only once the tensor has sent a reading (Guillaume, 2026-10-04:
 * "we'll add it once the tensor's job is done but displaying it empty right
 * now is very bad"). Loading, could not load and never-sent all render
 * nothing; a reading that has stopped updating keeps showing with its time,
 * marked as old.
 ****************/

export function RegimeWidget() {
  const { t } = useTranslation('dashboard');
  const { isAdmin } = useAuth();
  const { data } = useQuery<RegimeLiveResponse>({
    queryKey: ['regime', 'live'],
    queryFn: () => dashboardApi.getRegimeLive(),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  const r = data?.reading;
  if (!r || !regimeWidgetVisible(isAdmin, data?.widget_open)) return null;

  const value = (v: number | null | undefined) =>
    v === null || v === undefined ? t('regime.noValue') : t('regime.outOf100', { value: fmtFixed(v, 0) });
  const time = new Date(r.as_of).toLocaleString(intlLocale(), { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

  return (
    <section className="mb-3 rounded-xl border border-border bg-card px-4 py-3">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-sm font-semibold">{t('regime.title')}</h2>
        {isAdmin && !data?.widget_open && (
          <span className="text-[10px] text-muted-foreground">{t('regime.adminOnly')}</span>
        )}
      </div>

      <div className="grid grid-cols-3 gap-3 mt-2">
        <div>
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{t('regime.easyHard')}</p>
          <p className="text-lg font-semibold">{value(r.easy_hard)}</p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{t('regime.volatility')}</p>
          <p className="text-lg font-semibold">{value(r.volatility_intraday)}</p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{t('regime.regime')}</p>
          <p className="text-lg font-semibold">{t(phaseKey(r.phase_intraday))}</p>
          <p className="text-[10px] text-muted-foreground">
            {t('regime.multiday', { phase: t(phaseKey(r.phase_multiday)) })}
          </p>
        </div>
      </div>
      <p className="text-xs text-muted-foreground mt-2">{t('regime.explain')}</p>
      <p className={`text-[10px] mt-1 ${r.stale ? 'text-warning' : 'text-muted-foreground'}`}>
        {r.stale ? t('regime.readAtStale', { time }) : t('regime.readAt', { time })}
      </p>
    </section>
  );
}
